/**
 * Stripe ödeme akışının durumunu (başlatıldı / tamamlandı / hata) sekmeler
 * ve sayfa geçişleri arasında taşıyan küçük yardımcı katman.
 *
 * Ödeme yeni sekmede açıldığı için durum localStorage'da tutulur; kullanıcı
 * hangi sayfaya dönerse dönsün gerçek zamanlı bildirim doğru gösterilir.
 */
/** syncing = ödeme alındı, webhook/abonelik kaydı bekleniyor (gecikme fallback'i). */
export type CheckoutPhase = "started" | "syncing" | "completed" | "failed" | "canceled";

export interface CheckoutAttempt {
  tier: string | null;
  planLabel?: string | null;
  redirect?: string | null;
  phase: CheckoutPhase;
  /** Hata durumunda kullanıcıya gösterilecek kısa sebep kodu. */
  reason?: string | null;
  startedAt: number;
  updatedAt: number;
}

const KEY = "checkoutAttempt";
/** Bu süreden sonra hâlâ "started" ise ödeme tamamlanmamış sayılır (15 dk). */
export const CHECKOUT_STALE_MS = 15 * 60 * 1000;

const safeParse = (raw: string | null): CheckoutAttempt | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CheckoutAttempt;
    if (!parsed || typeof parsed.startedAt !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
};

export const readCheckoutAttempt = (): CheckoutAttempt | null => {
  try {
    return safeParse(localStorage.getItem(KEY));
  } catch {
    return null;
  }
};

const write = (attempt: CheckoutAttempt | null) => {
  try {
    if (!attempt) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, JSON.stringify(attempt));
  } catch {
    /* storage kapalı olabilir */
  }
  try {
    window.dispatchEvent(new CustomEvent("checkout-attempt-change"));
  } catch {
    /* SSR / test ortamı */
  }
};

export const markCheckoutStarted = (input: {
  tier?: string | null;
  planLabel?: string | null;
  redirect?: string | null;
}): CheckoutAttempt => {
  const now = Date.now();
  const attempt: CheckoutAttempt = {
    tier: input.tier ?? null,
    planLabel: input.planLabel ?? null,
    redirect: input.redirect ?? null,
    phase: "started",
    reason: null,
    startedAt: now,
    updatedAt: now,
  };
  write(attempt);
  return attempt;
};

const transition = (phase: CheckoutPhase, reason?: string | null) => {
  const current = readCheckoutAttempt();
  if (!current) return null;
  const next: CheckoutAttempt = { ...current, phase, reason: reason ?? null, updatedAt: Date.now() };
  write(next);
  return next;
};

export const markCheckoutSyncing = () => transition("syncing", "webhook_delay");
export const markCheckoutCompleted = () => transition("completed");
export const markCheckoutFailed = (reason?: string | null) => transition("failed", reason ?? "unknown");
export const markCheckoutCanceled = () => transition("canceled");
export const clearCheckoutAttempt = () => write(null);

/** Ödeme başlatıldı ama makul sürede sonuçlanmadıysa true döner. */
export const isCheckoutStale = (attempt: CheckoutAttempt | null, now = Date.now()) =>
  !!attempt &&
  (attempt.phase === "started" || attempt.phase === "syncing") &&
  now - attempt.startedAt > CHECKOUT_STALE_MS;

/** Ödeme sonucu hâlâ beklenirken kullanıcıya bekleme bildirimi gösterilir. */
export const isCheckoutPending = (attempt: CheckoutAttempt | null) =>
  !!attempt && (attempt.phase === "started" || attempt.phase === "syncing");
